import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Stethoscope } from 'lucide-react';
import { getDoctorById } from '../../redux/doctorSlice';
import CustomPrice from './CustomPrice';

// CUSTOM PRICE — the tenant tab, given the two things it cannot work out here.
//
// A custom price is a (patient, doctor, specialty, appointment type) row, so it
// only means anything once BOTH a hospital and a doctor are settled. In the
// tenant app both are implicit: the hospital is the one you signed in to, and
// the doctor is you. Super Admin has neither, so the hospital comes from the
// header picker and the doctor is chosen here.
//
// The doctor is not decoration, and since one-patient-row it is REQUIRED here.
// Two reasons, and the second is the new one:
//   * `selectedDoctor` is where the tenant tab reads the specialty catalogue
//     and the currency symbol from — without it every row renders as "Unknown"
//     at an unlabelled price, which reads like broken data rather than a
//     missing selection.
//   * The patientId is the PERSON now, and GET /custom-price/patient/:patientId
//     filters by that alone. A super_admin token is unscoped, so the old "All
//     doctors" option would have listed prices agreed at EVERY hospital under a
//     header naming one of them. Asking by doctor
//     (/patient/:patientId/doctor/:doctorId) keeps the answer inside the
//     hospital the picker chose, because a doctor belongs to one hospital.

const MocCustomPriceTab = ({ patientId, hospitalName, doctors = [], doctorId, onDoctorChange }) => {
    const { t } = useTranslation(['customPrice']);
    const dispatch = useDispatch();
    const { selectedDoctor } = useSelector((s) => s.doctor);

    useEffect(() => {
        if (doctorId && selectedDoctor?.doctorId !== doctorId) {
            dispatch(getDoctorById(doctorId));
        }
    }, [dispatch, doctorId, selectedDoctor?.doctorId]);

    if (!patientId) {
        return (
            <div className="rounded-2xl border border-gray-200 bg-white p-6 text-center">
                <Stethoscope className="mx-auto h-6 w-6 text-gray-300" />
                <p className="mt-2 text-sm font-semibold text-[#1a365d]">
                    {t('moc.pick_hospital_first', 'Pick a hospital first')}
                </p>
                <p className="mt-1 text-xs text-gray-500">
                    {t('moc.price_needs_hospital', 'Prices are agreed between a patient and a doctor at one hospital.')}
                </p>
            </div>
        );
    }

    return (
        <div className="mt-2">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3">
                <div className="flex items-center gap-2 min-w-0">
                    <Stethoscope className="h-4 w-4 text-[#4ABDBD] flex-shrink-0" />
                    <span className="text-xs font-semibold text-gray-600">
                        {t('moc.doctor_at', 'Doctor at')} {hospitalName || '—'}
                    </span>
                </div>
                <select
                    data-testid="moc-custom-price-doctor"
                    value={doctorId || ''}
                    onChange={(e) => onDoctorChange(e.target.value || null)}
                    className="sm:ml-auto w-full sm:w-72 px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]/40 focus:border-[#4ABDBD] cursor-pointer"
                >
                    <option value="">{t('moc.choose_doctor', 'Choose a doctor')}</option>
                    {doctors.map((d) => (
                        <option key={d.doctorId} value={d.doctorId}>
                            {[d.name || t('patientappointment.doctor_fallback', 'Doctor'), d.specialty].filter(Boolean).join(' · ')}
                        </option>
                    ))}
                </select>
            </div>

            {doctors.length === 0 && (
                <p className="mt-2 px-1 text-[11px] text-gray-400">
                    {t('moc.no_doctors_here', 'No doctor relationships recorded at this hospital yet, so specialty names and currency may be blank.')}
                </p>
            )}

            {doctorId ? (
                <CustomPrice patientId={patientId} doctorId={doctorId} />
            ) : (
                <div data-testid="moc-price-needs-doctor" className="mt-4 rounded-2xl border border-gray-200 bg-white p-6 text-center">
                    <Stethoscope className="mx-auto h-6 w-6 text-gray-300" />
                    <p className="mt-2 text-sm font-semibold text-[#1a365d]">
                        {t('moc.choose_doctor', 'Choose a doctor')}
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                        {t('moc.price_needs_doctor', 'A price is agreed between this patient and one doctor at one hospital, so the list is shown per doctor.')}
                    </p>
                </div>
            )}
        </div>
    );
};

export default MocCustomPriceTab;
